<template>
  <button class="submit-btn" 
    :disabled="loading || (disabled && !allowDisabledClick)"
    :class="[
      disabled || loading ? 'disabled text-text-disabled' : 'enabled text-white',
      { 'relative flex items-center gap-1 py-1 rounded-md': !$slots.default && !$scopedSlots.default },
      !$slots.default && !$scopedSlots.default ? icon ? 'pl-1.5 pr-2.5' : 'px-2.5' : ''
    ]"
    :style="{ '--button-color': disabled ? '#F6F8FA' : submitColor }"
    @click="$emit('click', $event)"
  >
    <!-- Button Content (if provided) -->
    <template v-if="$slots.default || $scopedSlots.default">
      <slot />
    </template>
    <!-- Default Layout (if no slot provided) -->
    <template v-else>
      <component v-if="icon" :is="icon" :width="20" :height="20" />
      <BaseText type="label" size="sm" class="transition-opacity duration-100" 
      :class="loading ? 'opacity-0' : 'opacity-100'">
        {{ text }}
      </BaseText>
      <transition>
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
          <BaseLoadingSpinnerCircle :duration="1" />
        </div>
      </transition>
    </template>
  </button>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BasePrimaryButton',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: 'Submit'
    },
    icon: {
      type: [Object, Function],
      required: false
    },
    theme: {
      type: String,
      required: false
    },
    // Show the disabled UI state when disabled = true, but still allow click events
    allowDisabledClick: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getTheme']),
    submitColor () {
      switch (this.theme ?? this.getTheme) {
        case 'blue': return '#1F69FF'
        case 'green': return '#009F6D'
        case 'purple': return '#6534DF'
        case 'red': return '#FF5453'
        default: return '#121212'
      }
    }
  }
}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
syntax: '<number>';
initial-value: 0.12;
inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #121212;
  inherits: false;
}
.submit-btn {
  --submit-color: var(--button-color);
  background: linear-gradient(
    180deg, 
    rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, 
    rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%
  ), var(--submit-color);
  box-shadow: 
    0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 
    0px 0px 0px 1px var(--submit-color);
  /* The fill fades via --submit-color, not background-color: Chrome can run background-color
  transitions on the compositor, which lets the fill finish before the main-thread text color */
  transition:
    box-shadow 25ms ease-in-out,
    color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  cursor: default;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>